/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2022 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers.  The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentUserIsGuest } from "@insite/client-framework/Store/Context/ContextSelectors";
import { OrderStateContext } from "@insite/client-framework/Store/Data/Orders/OrdersSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import LocalizedDateTime from "@insite/content-library/Components/LocalizedDateTime";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { xScreen } from "@stoneage/XDL/Utilities";
import React, { FC } from "react";
import { ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const currentUserIsGuest = getCurrentUserIsGuest(state);
    const userName = state.context.session.userName;

    return {
        isAuthenticated: userName && !currentUserIsGuest,
    };
};

const mapDispatchToProps = {};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface OrderDetailsHeaderInformationStyles {
    lineWrapper?: InjectableCss;
    valueText?: TypographyProps;
    titleText?: TypographyProps;
    containerStyledWrapper?: InjectableCss;
}

export const orderDetailsHeaderInformation: OrderDetailsHeaderInformationStyles = {
    containerStyledWrapper: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            margin-bottom: 36px;
            margin-top: 24px;

            ${xScreen("lte", "xs")} {
                width: 100%;
                &:first-child {
                    padding-right: 0;
                }
            }
            @media print {
                display: block;
            }
        `,
    },
    lineWrapper: {
        css: css`
            padding: 10px;
            width: calc(50% - 11px);
            display: flex;
            border-bottom: 1px solid ${getColor("common.ui5")};
            background-color: ${getColor("common.ui4")};
            @media print {
                display: inline-block;
                margin-right: -0.22rem;
                vertical-align: top;
            }
            ${xScreen("lte", "xs")} {
                width: 100%;
            }
            &:nth-child(4n + 3) {
                background-color: transparent;
            }
            &:nth-child(4n + 4) {
                background-color: transparent;
            }
            &:nth-child(odd) {
                margin-right: 22px;
                ${xScreen("lte", "xs")} {
                    margin-right: 0;
                    background-color: ${getColor("common.ui4")};
                }
            }
            &:nth-child(even) {
                ${xScreen("lte", "xs")} {
                    background-color: transparent;
                }
            }
        `,
    },
    titleText: {
        css: css`
            width: 132px;
            margin-right: 24px;
            display: inherit;
        `,
    },
    valueText: {
        css: css`
            font-weight: bold;
        `,
    },
};

const styles = orderDetailsHeaderInformation;

const OrderDetailsHeaderInformation: FC<Props> = () => {
    const { value: order } = React.useContext(OrderStateContext);
    if (!order) {
        return null;
    }
    const hasShippingMethod =
        (order.fulfillmentMethod === FulfillmentMethod.Ship || !order.fulfillmentMethod) && order.shipCode;
    return (
        <StyledWrapper {...styles.containerStyledWrapper}>
            {order.orderDate && (
                <StyledWrapper {...styles.lineWrapper}>
                    <Typography id="orderDate" {...styles.titleText}>
                        {translate("Order Date")}
                    </Typography>
                    <Typography aria-labelledby="orderDate" translate="no" data-test-selector="orderDate" {...styles.valueText}>
                        <LocalizedDateTime
                            dateTime={order.orderDate}
                            options={{ year: "numeric", month: "numeric", day: "numeric" }}
                        />
                    </Typography>
                </StyledWrapper>
            )}
            {order.statusDisplay && (
                <StyledWrapper {...styles.lineWrapper}>
                    <Typography {...styles.titleText} id="orderDetailsStatus">
                        {translate("Status")}
                    </Typography>
                    <Typography
                        {...styles.valueText}
                        aria-labelledby="orderDetailsStatus"
                        data-test-selector="orderDetailsStatus"
                    >
                        {order.statusDisplay}
                    </Typography>
                </StyledWrapper>
            )}
            {order.customerPO && (
                <StyledWrapper {...styles.lineWrapper}>
                    <Typography {...styles.titleText} id="orderPo">
                        {translate("PO #")}
                    </Typography>
                    <Typography translate="no" {...styles.valueText} aria-labelledby="orderPo">
                        {order.customerPO}
                    </Typography>
                </StyledWrapper>
            )}
            {order.terms && (
                <StyledWrapper {...styles.lineWrapper}>
                    <Typography as="h3" {...styles.titleText}>
                        {translate("Payment Terms")}
                    </Typography>
                    <Typography {...styles.valueText}>{order.terms}</Typography>
                </StyledWrapper>
            )}
            {hasShippingMethod && (
                <StyledWrapper {...styles.lineWrapper}>
                    <Typography {...styles.titleText}>{translate("Shipping Via")}</Typography>
                    <Typography {...styles.valueText} translate="no">{order.shipViaDescription || order.shipCode}</Typography>
                </StyledWrapper>
            )}
            {order.requestedDeliveryDateDisplay && (
                <StyledWrapper {...styles.lineWrapper}>
                    <Typography {...styles.titleText}>{translate("Ship by Date")}</Typography>
                    <Typography {...styles.valueText} translate="no" data-test-selector="orderDetails_requestedDeliveryDate">
                        <LocalizedDateTime
                            dateTime={new Date(order.requestedDeliveryDateDisplay)}
                            options={{ year: "numeric", month: "numeric", day: "numeric" }}
                        />
                    </Typography>
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

const orderDetailsHeaderInformationWidget: WidgetModule = {
    component: OrderDetailsHeaderInformation,
    definition: {
        group: "XNGAGE" as any,
    },
};

export default orderDetailsHeaderInformationWidget;
